import type { components } from "@/lib/api/schema.gen";

/**
 * Serializer-backed shapes sourced from the generated OpenAPI schema where
 * drf-spectacular emitted a component, plus hand-declared shapes for the
 * endpoints it can't infer.
 *
 * The partners API mixes generic views (PartnerGroup CRUD, SuperUser list/edit)
 * with plain APIViews and DRF `@action` methods whose request/response bodies
 * spectacular types loosely (e.g. `invite-member` is declared as taking a
 * PartnerGroup, `members` as returning one). For those we declare the true
 * shape here and cast the untyped result in api.ts — the same approach the
 * communications slice takes for its preview/review trees.
 */

// --- generated component types ----------------------------------------------

export type PartnerGroup = components["schemas"]["PartnerGroup"];
/** A live invitation as its invitee sees it — one dashboard notice each. */
export type MyInvitation = components["schemas"]["MyInvitation"];
export type PatchedPartnerGroup = components["schemas"]["PatchedPartnerGroup"];
export type PartnerGroupCreate = components["schemas"]["PartnerGroupCreate"];
export type SuperUser = components["schemas"]["SuperUser"];
export type PatchedSuperUser = components["schemas"]["PatchedSuperUser"];
export type LocalPartner = components["schemas"]["LocalPartner"];
export type PatchedLocalPartner = components["schemas"]["PatchedLocalPartner"];
export type Consent = components["schemas"]["Consent"];
export type PatchedConsent = components["schemas"]["PatchedConsent"];
export type DiscountType = components["schemas"]["DiscountTypeEnum"];

// --- backend enums (accounts.models) ----------------------------------------

/** Per-group role (PartnerGroupMembership.group_role). */
export const GroupRole = {
  Member: "member",
  Partner: "partner",
  LocalPartner: "local_partner",
} as const;
export type GroupRole = (typeof GroupRole)[keyof typeof GroupRole];

/** PartnerInvitation.invitation_type. */
export const InvitationType = {
  SuperUser: "super_user",
  LocalPartner: "local_partner",
} as const;
export type InvitationType =
  (typeof InvitationType)[keyof typeof InvitationType];

// --- hand-declared shapes (no generated component) --------------------------

/** PartnerInvitationSerializer output. */
export interface PartnerInvitation {
  id: string;
  invitation_type: InvitationType | null;
  partner_group: string | null;
  email: string;
  assigned_group_role: GroupRole | null;
  max_group_licenses: number | null;
  max_groups: number | null;
  discount_type: DiscountType | null;
  discount_percentage: string | null;
  discount_amount: string | null;
  discount_duration_years: number | null;
  language: string;
  group_name: string;
  pre_group_names: string[];
  expires_at: string | null;
  accepted_at: string | null;
  declined_at: string | null;
  licence_cap_at: string | null;
  created_at: string;
  /** Derived server-side: an answer (refusal, full group) wins over an expiry date. */
  status: "pending" | "expired" | "declined" | "licence_cap" | "accepted";
}

/** Body accepted by the `invite-member` action + `local-partners/invite/`. */
export interface InviteMemberBody {
  email: string;
  partner_group?: string;
  assigned_group_role?: GroupRole;
  /** Re-submit true to promote an existing user to local partner. */
  confirm_promote?: boolean;
  /** The message shown in the invite window, as the inviter left it. Blank
   * falls through to the group's wording. */
  email_body?: string;
  max_group_licenses?: number | null;
  max_groups?: number | null;
  discount_type?: DiscountType;
  discount_percentage?: string | null;
  discount_amount?: string | null;
  discount_duration_years?: number | null;
}

/** Body accepted by `super-users/invite/`. */
export interface SuperUserInviteBody {
  email: string;
  max_group_licenses?: number | null;
  max_groups?: number | null;
  discount_type?: DiscountType;
  discount_percentage?: string | null;
  discount_amount?: string | null;
  discount_duration_years?: number | null;
  language?: string;
  group_name?: string;
  pre_group_names?: string[];
}

/**
 * The `invite-member` action responds two ways: the created invitation (201),
 * or a confirmation prompt for a local partner who already has an account (200
 * with `requires_confirmation`). Inviting that account is the confirmed
 * re-submission; it is never promoted on the spot.
 */
export interface InvitePromoteConfirmation {
  requires_confirmation: true;
  detail: string;
  user_id: string;
  email: string;
  /** Who the account is, as the Django modal shows it. */
  name: string;
  enterprise: string | null;
  address: string | null;
  phone: string | null;
}

export type InviteMemberResult =
  | PartnerInvitation
  | InvitePromoteConfirmation;

export function isPromoteConfirmation(
  result: InviteMemberResult,
): result is InvitePromoteConfirmation {
  return (result as InvitePromoteConfirmation).requires_confirmation === true;
}

export function isInvitation(
  result: InviteMemberResult,
): result is PartnerInvitation {
  return "id" in result && "email" in result && "created_at" in result;
}

/** Response of `GET consents/{enterprise_profile_id}/` (partner view). */
export interface PartnerConsentStatus {
  enterprise_profile_id: string;
  enterprise_name: string;
  consent_progress_at: string | null;
  consent_diagnostic_at: string | null;
  consent_edit_at: string | null;
  has_view_consent: boolean;
}

/** The three member-controlled consent flags, keyed by their timestamp field. */
export type ConsentKey =
  | "consent_progress_at"
  | "consent_diagnostic_at"
  | "consent_edit_at";

/**
 * One row of the promo-code listing. Built from a Stripe response server-side,
 * so there is no model behind it — the shape is documented by
 * `api/serializers/partners.py::PromoCodeSerializer`.
 */
export type PromoCode = components["schemas"]["PromoCode"];

export type BackfillCandidate = components["schemas"]["BackfillCandidate"];

/**
 * A row from `GET partners/groups/{id}/members/` (PartnerGroupMembershipSerializer). Beyond
 * the identity fields: `user_enterprise_profile_id` is the `on_behalf_of`
 * target for viewing a member's data (null without a profile);
 * `has_external_license` is a licence granted by a *different* group;
 * `has_started_assessment` gates removal; `has_view_consent` gates the
 * on-behalf consultation links; `subscription_state` says why the member no
 * longer pays, if it doesn't — the member and its licence stay either way.
 */
export type PartnerGroupMembership = components["schemas"]["PartnerGroupMembership"];

export type SubscriptionState =
  components["schemas"]["PartnerGroupMembershipSubscriptionStateEnum"];

/**
 * Polled while a backfill scan runs. `state` is pending / ready / failed;
 * everything else only arrives once ready.
 */
export type BackfillStatus = components["schemas"]["BackfillStatus"];
